import { Component } from "react";
import { withTranslation } from "react-i18next";
import {
	CircularProgress,
	Card,
	Button,
	TextField,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	Divider,
	CardActions,
	Alert,
} from "@mui/material";
// cmp
import Na from "../na";
import RowEntry from "../row-entry";
import Toast from "../Toast";
// services
import Gupport from "../../services/gupport";
import User from "../../services/user";
import { Storage, StorageKeys } from "../../services/storage";
// import { merge } from "../../services/utils";
import { icons } from "@local/theme";
// types
import type { WithTranslation } from "react-i18next";
import type { UserId, UserData } from "../../types/user";
import type { IsoTimeStamp } from "../../types/misc";
import type { CmdCloudActionGupportResetUserTerms, CmdGetAudits, CmdGetB2CUser, CmdGetUser, CmdSaveUser, MsgResponseGetUser, MsgResponseGetB2CUser } from "../../types/gupport";

type Props = Readonly<WithTranslation & {
	userId: UserId;
}>;

type State = {
	loading: boolean;
	error: any;
	user: UserData | null;
	isExporting: boolean;
	exportStatus: "success" | "error" | null;
	acceptedDateRemoveStatus: "success" | "error" | null;
	saveButtonDisabled: boolean;
	saving: boolean;
	openErrorDialog: boolean;
	openAcceptedDateRemoveDialog: boolean;
	dialogErrorMsg: any;
	isRemovingAcceptedDate: boolean;
};

class UserGeneralInfo extends Component<Props, State> {

	constructor(props: Props) {
		super(props);

		this.state = {
			loading: true,
			error: null,
			user: null,
			isExporting: false,
			exportStatus: null,
			acceptedDateRemoveStatus: null,
			openAcceptedDateRemoveDialog: false,
			// inputErrors: {},
			saveButtonDisabled: true,
			// resetButtonDisabled: true,
			saving: false,
			// resettingPwd: false,
			openErrorDialog: false,
			// openResetDialog: false,
			dialogErrorMsg: null,
			isRemovingAcceptedDate: false,
			// password: null,
			// passwordConfirm: null
		};

		this.refresh = this.refresh.bind(this);
		// this.handleSelectChanged = this.handleSelectChanged.bind(this);
		this.handleFieldChanged = this.handleFieldChanged.bind(this);
		this.handleCreateDataExport = this.handleCreateDataExport.bind(this);
		this.handleCloseToast = this.handleCloseToast.bind(this);
		this.handleSaveClick = this.handleSaveClick.bind(this);
		// this.handleResetPasswordMailClick = this.handleResetPasswordMailClick.bind(this);
		// this.showResetDialog = this.showResetDialog.bind(this);
		// this.handlePasswordChanged = this.handlePasswordChanged.bind(this);
		// this.handlePasswordConfirmChanged = this.handlePasswordConfirmChanged.bind(this);
		// this.handleResetPasswordClick = this.handleResetPasswordClick.bind(this);
		this.handleCloseClick = this.handleCloseClick.bind(this);
		this.handleFetchUserResult = this.handleFetchUserResult.bind(this);
		this.handleDeleteClick = this.handleDeleteClick.bind(this);
		this.handleAcceptedDateRemoveClick = this.handleAcceptedDateRemoveClick.bind(this);
		this.handleAcceptedDateRemoveDialogCloseClick = this.handleAcceptedDateRemoveDialogCloseClick.bind(this);
		this.handleAcceptedDateRemoveToast = this.handleAcceptedDateRemoveToast.bind(this);
	}

	override componentDidMount() {
		this.fetchUser(this.props.userId);
	}

	refresh() {
		this.fetchUser(this.props.userId);
	}

	handleFetchUserResult(error: Error | null, msg: MsgResponseGetUser | MsgResponseGetB2CUser | undefined) {
		if (!error && msg?.payload.status === "ok") {
			this.setState({
				loading: false,
				error: error,
				user: msg.payload.data,
			});
		} else {
			this.setState({
				loading: false,
				error: error ?? msg?.payload.data,
				user: null,
			});
		}
	}

	fetchUser(userId: UserId) {
		if (Storage.get(StorageKeys.isAdvancedSearchEnabled)) {
			const cmd = {
				action: "getB2CUser",
				sub: userId,
			} as const satisfies CmdGetB2CUser;
			Gupport.send(cmd, this.handleFetchUserResult);
		} else {
			const cmd = {
				action: "getUser",
				username: userId,
			} as const satisfies CmdGetUser;
			Gupport.send(cmd, this.handleFetchUserResult);
		}
	}

	// handleSelectChanged(event) {
	// 	this.setState((prevState) => ({
	// 		user: merge(prevState.user, { gender: event.target.value })
	// 	}), () => {
	// 		this.checkInputFields();
	// 	});
	// }

	handleFieldChanged(event) {
		const name = event.target.name;
		const value = event.target.value;
		this.setState((prevState) => {
			const user = structuredClone(prevState.user);
			switch (name) {
				case "phone.country_code":
					user.phone.country_code = value;
					break;
				case "phone.number":
					user.phone.number = value;
					break;
				default:
					user[name] = value;
			}
			return {
				user: user,
			};
		}, () => {
			this.checkInputFields();
		});
	}

	checkInputFields() {
		const { t } = this.props;
		const inputErrors = {};
		let saveButtonDisabled = false;
		if (this.state.user.name.trim()) {
			inputErrors.name = null;
		} else {
			inputErrors.name = t("messages.required");
			saveButtonDisabled = true;
		}
		this.setState({
			// inputErrors: inputErrors,
			saveButtonDisabled: saveButtonDisabled,
		});
	}

	handleCreateDataExport() {
		this.setState({
			isExporting: true,
		});
		const cmd = {
			action: "getAudits",
			data: {
				userId: this.props.userId,
			},
		} as const satisfies CmdGetAudits;
		Gupport.send(cmd, (error, msg) => {
			this.setState({
				isExporting: false,
				exportStatus: (!error && msg?.payload.status === "ok") ? "success" : "error",
			});
		});
	}

	handleCloseToast() {
		this.setState({
			exportStatus: null,
		});
	}

	handleSaveClick() {
		const { t } = this.props;
		this.setState({
			saveButtonDisabled: true,
			saving: true,
		});
		const cmd = {
			action: "saveUser",
			...this.state.user,
		} as const satisfies CmdSaveUser;
		Gupport.send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				this.setState({
					saving: false,
				});
			} else {
				this.setState({
					openErrorDialog: true,
					dialogErrorMsg: t("users.notsaved"),
					saving: false,
				});
			}
		});
	}

	// handleResetPasswordMailClick() {
	// 	const { t, userId } = this.props;

	// 	// this.setState({
	// 	// 	resetting: true,
	// 	// });
	// 	const cmd = {
	// 		action: "sendUserPwdReset",
	// 		username: userId,
	// 	} as const satisfies CmdSendUserPwdReset;
	// 	Gupport.send(cmd, (error, msg) => {
	// 		if (!error && msg?.payload.status === "ok") {
	// 			// this.setState({
	// 			// 	resetting: false,
	// 			// });
	// 		} else {
	// 			this.setState({
	// 				openErrorDialog: true,
	// 				dialogErrorMsg: t("users.resetMailMsg"),
	// 				saving: false,
	// 			});
	// 		}
	// 	});
	// }

	// showResetDialog() {
	// 	this.setState({
	// 		resetButtonDisabled: true,
	// 		openResetDialog: true,
	// 		password: null,
	// 		passwordConfirm: null,
	// 	});
	// }

	// handlePasswordChanged(event) {
	// 	this.setState({
	// 		password: event.target.value,
	// 	}, () => {
	// 		this.checkPasswords();
	// 	});
	// }

	// handlePasswordConfirmChanged(event) {
	// 	this.setState({
	// 		passwordConfirm: event.target.value,
	// 	}, () => {
	// 		this.checkPasswords();
	// 	});
	// }

	// checkPasswords() {
	// 	this.setState({
	// 		resetButtonDisabled: !this.state.password || !this.state.passwordConfirm || this.state.password !== this.state.passwordConfirm,
	// 	});
	// }

	// handleResetPasswordClick() {
	// 	const { t, userId } = this.props;

	// 	this.setState({
	// 		resettingPwd: true,
	// 	});
	// 	const cmd = {
	// 		action: "setUserPwd",
	// 		username: userId,
	// 		password: md5(this.state.password),
	// 	} as const satisfies CmdSetUserPwd;
	// 	Gupport.send(cmd, (error, msg) => {
	// 		if (!error && msg?.payload.status === "ok") {
	// 			this.setState({
	// 				openResetDialog: false,
	// 				resettingPwd: false,
	// 			});
	// 		} else {
	// 			this.setState({
	// 				openErrorDialog: true,
	// 				dialogErrorMsg: t("users.resetFailedMsg"),
	// 				openResetDialog: false,
	// 				saving: false,
	// 				resettingPwd: false,
	// 			});
	// 		}
	// 	});
	// }

	handleCloseClick() {
		this.setState({
			openErrorDialog: false,
			dialogErrorMsg: null,
			// openResetDialog: false,
			// password: null,
			// passwordConfirm: null,
		});
	}

	handleAcceptedDateRemoveToast() {
		this.setState({
			acceptedDateRemoveStatus: null,
		});
	}

	handleAcceptedDateRemoveClick() {
		this.setState({
			openAcceptedDateRemoveDialog: true,
		});
	}

	handleDeleteClick() {
		this.setState({
			isRemovingAcceptedDate: true,
		});
		const cmd = {
			action: "cloudAction",
			module: "gupport",
			function: "reset_user_terms",
			params: [{
				userId: this.props.userId,
			}],
		} as const satisfies CmdCloudActionGupportResetUserTerms;
		Gupport.send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				this.fetchUser(this.props.userId);
				this.setState({
					acceptedDateRemoveStatus: "success",
					isRemovingAcceptedDate: false,
					openAcceptedDateRemoveDialog: false,
				});
			} else {
				this.setState({
					acceptedDateRemoveStatus: "error",
					isRemovingAcceptedDate: false,
				});
			}
		});
	}

	handleAcceptedDateRemoveDialogCloseClick() {
		this.setState({
			openAcceptedDateRemoveDialog: false,
		});
	}

	getEmail(user: UserData) {
		if (user.email) {
			return user.email;
		} else if (user.emails) {
			return user.emails.join(", ");
		} else {
			return user.username;
		}
	}

	getCreated(created: number | IsoTimeStamp | undefined) {
		if (!created) {
			return <Na />;
		}
		const date = (typeof created === "number") ? new Date(created / 1000) : new Date(created);
		return <time dateTime={date.toISOString()}>{date.toLocaleString()}</time>;
	}

	getUserTermsDate(isoDateTime: IsoTimeStamp | undefined) {
		if (isoDateTime === undefined) {
			return <Na />;
		}
		const date = new Date(isoDateTime);
		return <time dateTime={date.toISOString()}>{date.toLocaleString()}</time>;
	}

	override render() {
		const { t } = this.props;

		if (this.state.loading) {
			return <CircularProgress />;
		}
		if (this.state.error) {
			return <div>{this.state.error}</div>;
		}

		const isReadOnly = !Gupport.saveUser;
		// const isSendPwd = Boolean(Gupport.setUserPwd);
		// const isSendUserPwdReset = Boolean(Gupport.sendUserPwdReset);

		return (
			<Card className="card-separator card-info">
				<RowEntry title={t("users.username")}>
					{this.props.userId}
				</RowEntry>
				<Divider />
				<RowEntry title={t("users.email")}>
					{this.getEmail(this.state.user)}
				</RowEntry>
				<Divider />
				<RowEntry title={t("users.name")} styleRight={{ margin: "-8px 0" }}>
					{isReadOnly ? (this.state.user.name ?? <Na />) :
						<TextField
							name="name"
							placeholder={t("users.name")}
							fullWidth={true}
							value={this.state.user.name}
							onChange={this.handleFieldChanged}
						/>
					}
				</RowEntry>
				<Divider />
				<RowEntry title={t("users.givenName")}>
					{this.state.user.given_name ?? <Na />}
				</RowEntry>
				<Divider />
				<RowEntry title={t("users.familyName")}>
					{this.state.user.family_name ?? <Na />}
				</RowEntry>
				<Divider />
				<RowEntry title={t("users.created")}>
					{this.getCreated(this.state.user.created)}
				</RowEntry>
				<Divider />
				<RowEntry title={t("users.country")}>
					{this.state.user.country ?? <Na />}
				</RowEntry>
				{User.hasLevel("user_read") &&
					<>
						<Divider />
						<RowEntry title={t("users.dataExport")}>
							<Button
								variant="contained"
								startIcon={<icons.CloudDownload />}
								loading={this.state.isExporting}
								loadingPosition="start"
								onClick={this.handleCreateDataExport}
							>
								{t("users.createDataExport")}
							</Button>
						</RowEntry>
						<Toast
							autoHideDuration={(this.state.exportStatus === "success") ? 3000 : 6000}
							severity={(this.state.exportStatus === "success") ? "success" : "error"}
							open={this.state.exportStatus !== null}
							onClose={this.handleCloseToast}
							message={t((this.state.exportStatus === "success") ? "users.dataExportSuccess" : "users.dataExportError")}
						/>
					</>
				}
				<Divider />
				<RowEntry title={t("users.termsAndConditionsAcceptedDate")}>
					{this.getUserTermsDate(this.state.user?.user_terms?.date)}
					{(typeof this.state.user?.user_terms?.date === "string") &&
						<>
							<Button
								variant="contained"
								startIcon={<icons.Delete />}
								onClick={this.handleAcceptedDateRemoveClick}
								sx={{
									marginLeft: 2,
								}}
							>
								{t("users.removeDate")}
							</Button>
							<Toast
								autoHideDuration={(this.state.acceptedDateRemoveStatus === "success") ? 3000 : 6000}
								severity={(this.state.acceptedDateRemoveStatus === "success") ? "success" : "error"}
								open={this.state.acceptedDateRemoveStatus !== null}
								onClose={this.handleAcceptedDateRemoveToast}
								message={t((this.state.acceptedDateRemoveStatus === "success") ? "users.acceptedDateRemoveSuccess" : "users.acceptedDateRemoveError")}
							/>
						</>
					}
				</RowEntry>
				<Divider />
				{/*<TableRow>
							<TableCell style={columnStyle}>{t("users.gender")}</TableCell>
							<TableCell>
								{
									isReadOnly
										? this.state.user.gender || <Na />
										: <Select
											name="gender"
											value={this.state.user.gender}
											onChange={this.handleSelectChanged}
										>
											<MenuItem value="" primaryText="" />
											<MenuItem value="male" primaryText={t("users.male")} />
											<MenuItem value="female" primaryText={t("users.female")} />
										</Select>
								}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell style={columnStyle}>{t("users.phone")}</TableCell>
							<TableCell>
								{
									isReadOnly
										? this.state.user.phone ? `${this.state.user.phone.country_code }${this.state.user.phone.number} : <Na />
										: <>
											<TextField
												name="phone.country_code"
												placeholder={t("users.countryCode")}
												fullWidth={false}
												value={this.state.user.phone ? this.state.user.phone.country_code : null}
												onChange={this.handleFieldChanged}
												style={{width: "30%"}}
											/>
											<TextField
												name="phone.number"
												placeholder={t("users.number")}
												fullWidth={false}
												value={this.state.user.phone ? this.state.user.phone.number : null}
												onChange={this.handleFieldChanged}
											/>
										</>
								}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell style={columnStyle}>{t("users.city")}</TableCell>
							<TableCell>
								{
									isReadOnly
										? this.state.user.city || <Na />
										: <TextField
											name="city"
											placeholder={t("users.city")}
											fullWidth={true}
											value={this.state.user.city}
											onChange={this.handleFieldChanged}
											//errorText={this.state.inputErrors.city}
										/>
								}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell style={columnStyle}>{t("users.region")}</TableCell>
							<TableCell>
								{
									isReadOnly
										? this.state.user.region || <Na />
										: <TextField
											name="region"
											placeholder={t("users.region")}
											fullWidth={true}
											value={this.state.user.region}
											onChange={this.handleFieldChanged}
										/>
								}
							</TableCell>
						</TableRow>
						<TableRow>
							<TableCell style={columnStyle}>{t("users.country")}</TableCell>
							<TableCell>
								{
									isReadOnly
										? this.state.user.country || <Na />
										: <TextField
											name="country"
											placeholder={t("users.country")}
											fullWidth={true}
											value={this.state.user.country}
											onChange={this.handleFieldChanged}
										/>
								}
							</TableCell>
						</TableRow>*/}
				<CardActions style={{ float: "right" }}>
					{!isReadOnly &&
						<Button
							variant="contained"
							disabled={this.state.saveButtonDisabled}
							loading={this.state.saving}
							onClick={this.handleSaveClick}
						>
							{t("messages.save")}
						</Button>
					}
				</CardActions>
				{/*<TableRow>
							<TableCell style={columnStyle}></TableCell>
							<TableCell></TableCell>
						</TableRow>
						{(isSendUserPwdReset || isSendPwd) && <TableRow>
							<TableCell style={columnStyle}>{t("users.password")}</TableCell>
							<TableCell>
								{isSendUserPwdReset &&
								<RaisedButton label={t("users.resetMail")} primary={true} onClick={this.handleResetPasswordMailClick} />
								}
								{isSendPwd &&
									<Button
										variant="contained"
										loading={this.state.resetting}
										onClick={this.showResetDialog}
										style={{marginLeft: isSendUserPwdReset ? "10px" : 0}}
									>
										{t("users.resetPwd")}
									</Button>
								}
							</TableCell>
						</TableRow>*/}
				<Dialog
					fullWidth={true}
					open={this.state.openErrorDialog}
					onClose={this.handleCloseClick}
				>
					<DialogTitle>{t("dialog.error")}</DialogTitle>
					<DialogContent>
						<DialogContentText>
							{this.state.dialogErrorMsg}
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={this.handleCloseClick}>{t("dialog.ok")}</Button>
					</DialogActions>
				</Dialog>
				<Dialog
					fullWidth={true}
					open={this.state.openAcceptedDateRemoveDialog}
					onClose={this.handleCloseClick}
				>
					<DialogTitle>{t("dialog.acceptedDateRemoveDialog.title")}</DialogTitle>
					<DialogContent>
						<DialogContentText>
							<Alert
								severity="warning"
								variant="outlined"
							>
								{t("dialog.acceptedDateRemoveDialog.subtitle")}
							</Alert>
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button color="danger" loading={this.state.isRemovingAcceptedDate} onClick={this.handleDeleteClick}>{t("dialog.delete")}</Button>
						<Button color="inherit" onClick={this.handleAcceptedDateRemoveDialogCloseClick}>{t("dialog.cancel")}</Button>
					</DialogActions>
				</Dialog>
				{/* <Dialog
					fullWidth={true}
					open={this.state.openResetDialog}
					onClose={this.handleCloseClick}
				>
					<DialogTitle>{t("users.resetPwdDialogTitle")}</DialogTitle>
					<DialogContent>
						<DialogContentText>
							{t("users.resetDialogMsg")} {this.state.user.name} ({this.props.userId}):
							<TextField
								type="password"
								placeholder={t("users.newPwd")}
								fullWidth={true}
								autoComplete="new-password"
								value={this.state.password}
								onChange={this.handlePasswordChanged}
							/>
							<TextField
								type="password"
								placeholder={t("users.confirmPwd")}
								fullWidth={true}
								autoComplete="new-password"
								value={this.state.passwordConfirm}
								onChange={this.handlePasswordConfirmChanged}
							/>
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button
							disabled={this.state.resetButtonDisabled}
							loading={this.state.resettingPwd}
							onClick={this.handleResetPasswordClick}
						>
							{t("messages.reset")}
						</Button>
						<Button color="inherit" onClick={this.handleCloseClick}>{t("dialog.cancel")}</Button>
					</DialogActions>
				</Dialog> */}
			</Card>
		);
	}

}

export default withTranslation()(UserGeneralInfo);
